import { FontAwesomeIcon, Stack, Text, Title } from "@skailer-com/uikit";
import { type ClipboardEvent, type KeyboardEvent, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

const splitValue = (value: string, length: number) =>
  Array.from({ length }, (_, index) => value[index] ?? "");

export interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  label?: string;
  caption?: string;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  onComplete?: (value: string, fromPaste: boolean) => void;
}

export function CodeInput({
  value,
  onChange,
  length = 6,
  label,
  caption,
  error,
  disabled = false,
  autoFocus = false,
  onComplete,
}: CodeInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const [boxes, setBoxes] = useState<string[]>(() => splitValue(value, length));
  const [synced, setSynced] = useState({ value, length });

  if (synced.value !== value || !Object.is(synced.length, length)) {
    setSynced({ value, length });
    if (boxes.join("") !== value) setBoxes(splitValue(value, length));
  }

  const focusBox = (index: number) => {
    const clamped = Math.max(0, Math.min(index, length - 1));
    inputsRef.current[clamped]?.focus();
    inputsRef.current[clamped]?.select();
  };

  const commit = (next: string[], fromPaste = false) => {
    setBoxes(next);
    const joined = next.join("");
    onChange(joined);
    if (joined.length === length) onComplete?.(joined, fromPaste);
  };

  const fillFrom = (start: number, chars: string) => {
    const clean = chars.replace(/\D/g, "");
    if (!clean) return;

    const next = boxes.slice();
    let cursor = start;
    for (const char of clean) {
      if (cursor >= length) break;
      next[cursor] = char;
      cursor += 1;
    }
    commit(next, true);
    if (cursor < length) focusBox(cursor);
  };

  const handleChange = (index: number, raw: string) => {
    const clean = raw.replace(/\D/g, "");

    if (!clean) {
      const next = boxes.slice();
      next[index] = "";
      commit(next);
      return;
    }

    if (clean.length > 1) {
      fillFrom(index, clean);
      return;
    }

    const next = boxes.slice();
    next[index] = clean;
    commit(next);
    if (index + 1 < length) focusBox(index + 1);
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace") {
      if (boxes[index]) {
        const next = boxes.slice();
        next[index] = "";
        commit(next);
      } else if (index > 0) {
        const next = boxes.slice();
        next[index - 1] = "";
        commit(next);
        focusBox(index - 1);
      }
      event.preventDefault();
    } else if (event.key === "ArrowLeft") {
      focusBox(index - 1);
      event.preventDefault();
    } else if (event.key === "ArrowRight") {
      focusBox(index + 1);
      event.preventDefault();
    }
  };

  const handlePaste = (index: number, event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    fillFrom(index, event.clipboardData.getData("text"));
  };

  return (
    <Stack gap={"smd"}>
      {(label || caption) && <Title size={"xs"} header={label} caption={caption} />}
      <div className={"flex gap-2"}>
        {boxes.map((digit, index) => (
          <div key={index} className={"relative flex-1"}>
            <input
              ref={(el) => {
                inputsRef.current[index] = el;
              }}
              type={"text"}
              inputMode={"numeric"}
              autoComplete={index === 0 ? "one-time-code" : "off"}
              maxLength={1}
              disabled={disabled}
              autoFocus={autoFocus && index === 0}
              value={digit}
              onFocus={(event) => event.target.select()}
              onChange={(event) => handleChange(index, event.target.value)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              onPaste={(event) => handlePaste(index, event)}
              className={twMerge(
                "h-14 w-full rounded-xs bg-dark/10 text-center text-2xl font-medium leading-none tracking-[0.07em] text-default",
                "focus:border focus:border-dark/30 caret-transparent",
                disabled && "opacity-60"
              )}
            />
            {!digit && (
              <span
                className={twMerge(
                  "pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-secondary"
                )}
              >
                <FontAwesomeIcon icon={"fa-light fa-circle"} />
              </span>
            )}
          </div>
        ))}
      </div>
      {error && (
        <Text size={"sm"} className={"text-alert"}>
          {error}
        </Text>
      )}
    </Stack>
  );
}
